import styled from 'styled-components';
import { Icon, IconButton, ThemeProvider } from '@workramp/ui';
import { ActionMessageTypes } from './action-messages';

const variants = {
  [ActionMessageTypes.default]: {
    backgroundColor: '#f2f8ea',
    borderColor: '#dfeccb',
    color: '#618a3d',
    icon: <Icon name="check-circle:regular" color="currentColor" />,
  },
  [ActionMessageTypes.warning]: {
    backgroundColor: '#fef8e3',
    borderColor: '#fcedba',
    color: '#f09536',
    icon: <Icon name="warning:regular" color="currentColor" />,
  },
  [ActionMessageTypes.error]: {
    backgroundColor: '#fcf0f1',
    borderColor: '#f8d7d9',
    color: '#510a09',
    icon: <Icon name="warning:regular" color="currentColor" />,
  },
};

/**
 * @param {object} props
 * @param {boolean} props.isVisible
 * @param {React.ReactNode} props.message
 * @param {string} [props.position=bottomLeft]
 * @param {string} [props.type=ActionMessageTypes.default]
 * @param {function} props.onDismiss
 * @param {function} [props.onUndo]
 * @param {string} [props.locateUrl]
 * @deprecated prefer `import { Toast } from '@workramp/ui';`
 */
export default function ActionMessage({
  isVisible,
  message,
  position = 'bottomLeft',
  type = ActionMessageTypes.default,
  onDismiss,
  onUndo,
  locateUrl,
}) {
  return (
    <ThemeProvider>
      <Wrapper isVisible={isVisible} type={type} position={position} aria-hidden={!isVisible}>
        <IconWrapper>{variants[type].icon}</IconWrapper>

        <Message>{message}</Message>

        {locateUrl && <Link href={locateUrl}>Locate</Link>}
        {onUndo && (
          <Button type="button" onClick={onUndo}>
            Undo
          </Button>
        )}

        <IconButton
          label="Close"
          iconName="x-small:regular"
          variant="text"
          color="secondary"
          size="sm"
          onPress={onDismiss}
        />
      </Wrapper>
    </ThemeProvider>
  );
}

const positions = {
  bottomRight: `
    bottom: 32px;
    right: 32px;
  `,
  topRight: `
    top: 32px;
    right: 32px;
  `,
  bottomLeft: `
    bottom: 32px;
    left: 32px;
  `,
  topLeft: `
    top: 32px;
    left: 32px;
  `,
};

const Wrapper = styled.div`
  position: fixed;
  display: flex;
  align-items: center;
  max-width: 480px;
  padding: 8px 16px;
  font-size: 14px;
  line-height: 1.5;
  background-color: ${({ type }) => variants[type].backgroundColor};
  border-radius: 8px;
  border: 1px solid ${({ type }) => variants[type].borderColor};
  color: ${({ type }) => variants[type].color};
  z-index: 1050;
  ${({ position }) => positions[position]}
  transition: opacity linear 0.3s;
  opacity: 0;
  ${({ isVisible }) => (isVisible ? 'opacity: 1;' : '')}

  & > * + * {
    margin-left: 4px;
  }
`;

const Message = styled.div`
  display: inline-block;
  margin-left: 12px;
`;

const Button = styled.button`
  font-weight: bold;
  background-color: transparent;
  border: none;
  padding: 8px;
  line-height: 1;
`;

const Link = styled.a`
  &:link {
    color: currentColor;
    display: inline-block;
    padding: 8px;
    font-weight: bold;
    line-height: 1;
  }
`;

const IconWrapper = styled.div`
  height: 16px;
`;
